import { Inject, Injectable, InjectionToken } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { environment } from '../../environment';

/**
 * CheckoutService — infrastructure PARTAGÉE du départ Stripe Checkout (#1677).
 *
 * 🔴 MONEY PATH. Ce service ne porte AUCUNE règle métier : ni prix, ni poids,
 * ni éligibilité, ni promotions, ni écriture Firestore. Il mutualise trois
 * mécaniques strictement techniques, jusqu'ici dupliquées entre
 * `features/ship/pages/order-summary` et `features/assisted` :
 *
 *   1. le POST HTTP vers `environment.apiUrls.createInvoice` ;
 *   2. l'ajout **best-effort** du Bearer token Firebase (#426) ;
 *   3. la redirection (`sessionUrl` d'abord, repli Stripe.js `sessionId`).
 *
 * ⚠️ Les erreurs serveur remontent **INTACTES** : aucun `catchError`, aucun
 * repli qui rendrait `success: true`. Les codes métier (`EMAIL_REQUIRED`,
 * `ORDERING_CLOSED`, `SHIPPING_OPTION_RETIRED`, `PRICE_RECOMPOSITION_FAILED`…)
 * doivent atteindre le composant appelant, qui seul décide du texte traduit.
 *
 * ⚠️ `providedIn: 'root'` = singleton. Le service est volontairement SANS ÉTAT
 * (hors mémoïsation du chargement de Stripe.js, idempotent) : rien ne peut
 * fuiter d'un tunnel d'envoi au suivant.
 */

/** Charge Stripe.js — point d'injection pour les tests. */
export type StripeJsLoader = () => Promise<Stripe | null>;

/** Quitte l'application vers une URL externe — point d'injection pour les tests. */
export type CheckoutNavigator = (url: string) => void;

export const STRIPE_JS_LOADER = new InjectionToken<StripeJsLoader>('STRIPE_JS_LOADER', {
  providedIn: 'root',
  factory: () => () => loadStripe(environment.stripePublicKey)
});

export const CHECKOUT_NAVIGATOR = new InjectionToken<CheckoutNavigator>('CHECKOUT_NAVIGATOR', {
  providedIn: 'root',
  factory: () => (url: string) => { window.location.href = url; }
});

export interface CheckoutParcelPayload {
  weight: number;
  dimensions: {
    length: number;
    width: number;
    height: number;
  };
  value?: number;
}

export interface CheckoutSenderPayload {
  name: string;
  address: string;
  houseNumber?: string;
  city: string;
  postalCode: string;
  country: string;
  addressComplement?: string;
  inputMode?: string;
  phone?: string;
  phonePrefix?: string;
  email?: string;
}

/**
 * Corps envoyé à `createInvoice`. Miroir front de `CreateInvoiceRequest`
 * (`functions/src/models/stripe.models.ts`) — le serveur reste juge du montant,
 * des promotions et des options ; ce type ne fait que nommer le contrat.
 *
 * `metadata` reste volontairement ouvert : le webhook lit des champs sérialisés
 * (`logisticsContext`, `quickData`) qui diffèrent d'un parcours à l'autre.
 */
export interface CreateCheckoutSessionRequest {
  email?: string;
  parcel: CheckoutParcelPayload;
  amount: number;
  phoneNumber?: string;
  sender?: CheckoutSenderPayload;
  pType?: string;
  language?: string;
  promoCode?: string;
  senderCountry?: string;
  /** URLs de retour propres au parcours (le flux assisté les surcharge). */
  successUrl?: string;
  cancelUrl?: string;
  metadata?: Record<string, unknown>;
}

/** Réponse de `createInvoice` (cf. `CreateInvoiceResponse` côté functions). */
export interface CreateCheckoutSessionResponse {
  success?: boolean;
  sessionId?: string;
  sessionUrl?: string;
  locale?: string;
  paymentMethods?: string[];
  optimizationPhase?: string;
  version?: string;
}

export type CheckoutErrorCode =
  | 'INCOMPLETE_SESSION_RESPONSE'
  | 'STRIPE_JS_UNAVAILABLE'
  | 'STRIPE_REDIRECT_FAILED';

/**
 * Panne d'INFRASTRUCTURE du départ Checkout — jamais un refus métier.
 * Un refus métier remonte tel quel dans le `HttpErrorResponse` d'origine.
 */
export class CheckoutError extends Error {
  constructor(readonly code: CheckoutErrorCode, message: string) {
    super(message);
    this.name = 'CheckoutError';
  }
}

@Injectable({ providedIn: 'root' })
export class CheckoutService {

  /** Mémoïsation du chargement de Stripe.js (idempotent, sans état métier). */
  private stripePromise: Promise<Stripe | null> | null = null;

  constructor(
    private http: HttpClient,
    private fireAuth: Auth,
    @Inject(STRIPE_JS_LOADER) private stripeLoader: StripeJsLoader,
    @Inject(CHECKOUT_NAVIGATOR) private redirectExternal: CheckoutNavigator
  ) {}

  /**
   * Crée la session Stripe côté serveur.
   *
   * ⚠️ Aucun `catchError` ici, et c'est délibéré : tout `HttpErrorResponse`
   * remonte à l'appelant sans être aplati, pour que `error.error.code` reste
   * lisible (#331-B6, #1182, #1393). Une erreur avalée sur ce chemin, c'est un
   * paiement qui échoue en silence.
   */
  async createSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    const options = await this.buildRequestOptions();

    const response = await firstValueFrom(
      this.http.post<CreateCheckoutSessionResponse>(
        environment.apiUrls.createInvoice,
        request,
        options
      )
    );

    // Le serveur a répondu 2xx sans session exploitable : échouer proprement
    // plutôt que rediriger vers `undefined` ou écrire un sessionId vide.
    if (!response?.sessionId && !response?.sessionUrl) {
      throw new CheckoutError(
        'INCOMPLETE_SESSION_RESPONSE',
        'createInvoice a répondu sans sessionId ni sessionUrl'
      );
    }

    return response;
  }

  /**
   * Redirige le navigateur vers Stripe Checkout.
   * `sessionUrl` (URL canonique renvoyée par Stripe) d'abord ; repli Stripe.js
   * sur `sessionId` quand le serveur ne l'a pas fournie.
   */
  async redirectToCheckout(session: CreateCheckoutSessionResponse): Promise<void> {
    if (session?.sessionUrl) {
      this.redirectExternal(session.sessionUrl);
      return;
    }

    if (!session?.sessionId) {
      throw new CheckoutError(
        'INCOMPLETE_SESSION_RESPONSE',
        'Aucune sessionUrl ni sessionId pour rediriger vers Stripe Checkout'
      );
    }

    const stripe = await this.loadStripeJs();
    if (!stripe) {
      // Sans cette erreur, l'ancien `if (stripe) { … }` laissait le spinner
      // tourner indéfiniment : paiement bloqué, aucun signal.
      throw new CheckoutError('STRIPE_JS_UNAVAILABLE', 'Stripe.js indisponible');
    }

    const { error } = await stripe.redirectToCheckout({ sessionId: session.sessionId });
    if (error) {
      throw new CheckoutError('STRIPE_REDIRECT_FAILED', error.message || 'Redirection Stripe refusée');
    }
  }

  /**
   * #426 — joindre l'ID token : `createInvoice` résout l'identité depuis le
   * Bearer (jamais depuis le body) pour les promos à limite par utilisateur
   * (fail-closed sans token). Best-effort : sans utilisateur connecté (cas
   * courant du flux assisté via lien SMS) ou en cas d'échec du token, on poste
   * sans header — un paiement sans promo limitée reste fonctionnel.
   */
  private async buildRequestOptions(): Promise<{ headers?: Record<string, string> }> {
    const idToken = await this.readIdToken();
    return idToken ? { headers: { Authorization: `Bearer ${idToken}` } } : {};
  }

  private async readIdToken(): Promise<string | null> {
    try {
      return (await this.fireAuth.currentUser?.getIdToken()) ?? null;
    } catch {
      return null;
    }
  }

  private loadStripeJs(): Promise<Stripe | null> {
    if (!this.stripePromise) {
      // Un échec de chargement ne doit pas être mémoïsé : le client peut
      // relancer le paiement après annulation (onRetryPayment).
      this.stripePromise = Promise.resolve(this.stripeLoader())
        .catch((err) => { this.stripePromise = null; throw err; });
    }
    return this.stripePromise;
  }
}
